import { useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { useSnackbar } from "notistack";
import { chanePassword } from "src/DAL/Login/Login";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrDialog from "src/design-system/components/GrDialog";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrTextField from "src/design-system/components/GrTextField";

const emptyInputs = { password: "", repeatPassword: "", oldPassword: "" };

const fieldsSx = {
  display: "flex",
  flexDirection: "column",
  gap: gr.space[4],
  mt: gr.space[4],
};

export default function ChangePassword({
  openPasswordModal,
  setopenPasswordModal,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState(emptyInputs);
  // Names of the fields whose text is shown in plain text (eye toggle).
  const [shown, setShown] = useState({});

  // Closing (cancel, X, backdrop or after a successful save) clears the typed passwords.
  const handleClose = () => {
    setopenPasswordModal(false);
    setInputs(emptyInputs);
    setShown({});
  };

  const toggleShown = (name) =>
    setShown((values) => ({ ...values, [name]: !values[name] }));

  const passwordProps = (name, label) => ({
    name,
    label,
    type: shown[name] ? "text" : "password",
    autoComplete: name === "oldPassword" ? "current-password" : "new-password",
    value: inputs[name],
    onChange: handleChange,
    required: true,
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <GrIconButton
            variant="ghost"
            size="sm"
            aria-label={shown[name] ? `Hide ${label}` : `Show ${label}`}
            onClick={() => toggleShown(name)}
            edge="end"
          >
            <GrIcon icon={shown[name] ? icons.eyeOff : icons.eye} />
          </GrIconButton>
        </InputAdornment>
      ),
    },
  });

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (inputs.password !== inputs.repeatPassword) {
      enqueueSnackbar("Password And Repeat Password not matched", {
        variant: "error",
      });
      setIsLoading(false);
    } else {
      const formData = new FormData();
      formData.append("old_password", inputs.oldPassword);
      formData.append("password", inputs.password);
      formData.append("confirm_password", inputs.repeatPassword);
      const result = await chanePassword(formData);
      if (result.code === 200) {
        setIsLoading(false);
        handleClose();
        enqueueSnackbar("Password Changed Successfully", {
          variant: "success",
        });
      } else {
        setIsLoading(false);
        enqueueSnackbar(result.message, { variant: "error" });
      }
    }
  };

  const handleChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  return (
    <GrDialog
      open={openPasswordModal}
      onClose={handleClose}
      title="Change Password"
      maxWidth={420}
    >
      <GrIconButton
        variant="ghost"
        size="sm"
        aria-label="Close"
        onClick={handleClose}
        sx={{ position: "absolute", top: gr.space[4], right: gr.space[4] }}
      >
        <GrIcon icon={icons.close} />
      </GrIconButton>
      <form onSubmit={handlePasswordSubmit}>
        <Box sx={fieldsSx}>
          <GrTextField
            autoFocus
            {...passwordProps("oldPassword", "Old Password")}
          />
          <GrTextField {...passwordProps("password", "New Password")} />
          <GrTextField
            {...passwordProps("repeatPassword", "Repeat New Password")}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: gr.space[3],
            mt: gr.space[6],
          }}
        >
          <GrButton variant="quiet" type="button" onClick={handleClose}>
            Cancel
          </GrButton>
          <GrButton type="submit">{isLoading ? "Saving..." : "Save"}</GrButton>
        </Box>
      </form>
    </GrDialog>
  );
}
